import { SectionProps } from '../sections/SectionProps';
import { Controller } from 'react-hook-form';
import { useCallback } from 'react';
import { DownloadLink } from '@ofqual/portal-components/components/DownloadLink';
import { Grid, Typography } from '@material-ui/core';
import { SocItemDetailsAo } from '../../domain/SocItemDetailsAo';
import { FileInput } from '@ofqual/portal-components/components/FileInput';
import { useAuth } from '@ofqual/portal-core/core/hooks';
import {
  aoSocActiveSubmissionsSelector,
  authSelector,
  itemYearSelector,
  socItemReferenceNumberSelector,
} from '../../redux/selectors';
import { UserType } from '../../redux/types';
import { socService } from '../../services';
import { useSelector } from 'react-redux';
import { Error } from "../common/Error";
import { MAX_TOTAL_SIZE_MB, validateFiles } from "../utils/fileSizeLimit"

type KloeProps = {
  sectionProps: SectionProps<SocItemDetailsAo>;
  kloeType: 'KLOE1' | 'KLOE2' | 'KLOE3';
};

export const KloeFileUpload: React.FC<KloeProps> = ({
  sectionProps,
  kloeType
}) => {

  const { userType, token } = useAuth(authSelector);
  const socItemReferenceNumber = useSelector(socItemReferenceNumberSelector);
  const { control, errors, disabled } = sectionProps;
  const kloeTypeAsInt = ['KLOE1', 'KLOE2', 'KLOE3'].indexOf(kloeType) + 1;;
  const activeFiles = useSelector(aoSocActiveSubmissionsSelector);
  const year = useSelector(itemYearSelector) ?? "2024"
  const submissionFile = activeFiles?.find((item) => item.submissionType === `KLOE${kloeTypeAsInt}` && item.active)

  const downloadBlob = useCallback(
    (year: string, fileItemId: string, socId: string) =>
      socService.downloadSocFiles(
        year,
        fileItemId,
        socId,
        { token }
      ), [token]
  );

  // If there is a file in SocActiveSubmissions of the Type passed in to Section Props
  // Load a download link for that file

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6">
          Download and complete the KLOE {kloeTypeAsInt} template response form
        </Typography>
        <DownloadLink
          fileName={`${kloeType}-${socItemReferenceNumber}.docx`}
          getBlob={() =>
            socService.getSocTemplate(year, kloeType, {
              token: token,
            })
          }
        />
        <Typography variant="h6">Upload your completed response form</Typography>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <Controller
            control={control}
            name={`kloe${kloeTypeAsInt}Attachment`}
            rules={{ required: submissionFile === undefined, validate : validateFiles }}
            disabled={disabled}
            as={
              <FileInput
                accept=".docx"
                dataTest={`kloe${kloeTypeAsInt}-add-attachment-button`}
                disabled={userType === UserType.Ofqual}
              />
            }
            onChange={([event]: any) => {
              const target = event.target as HTMLInputElement;
              return target.files ? Array.from(target.files) : [];
            }}
          />
          {submissionFile &&
            <div style={{ marginTop: "5px" }}>
              <DownloadLink
                fileName={submissionFile!.fileName}
                getBlob={() => downloadBlob(year, submissionFile!.id, submissionFile!.socId)}
              />
            </div>
          }
        </div>

        {errors[`kloe${kloeTypeAsInt}Attachment`]  && (
          <div style={{ marginTop: '15px' }}>
            {errors[`kloe${kloeTypeAsInt}Attachment`] .type === "validate" && (
              <Error data-test="questionon-file-validation-error">
                The file must be smaller than {MAX_TOTAL_SIZE_MB}MB
              </Error>
            )}
            {errors[`kloe${kloeTypeAsInt}Attachment`] .type === "required" && (
              <Error data-test="questionon-file-required-error">
                Please select a file to attach
              </Error>
            )}
          </div>
        )}
        
      </Grid>
    </>
  );
};